import {Component, OnInit} from '@angular/core';
import {AssistanceApplication, AssistanceApplicationType, Client, InternalDraftApplicationViewModel, User, UserType} from '../../../../../../services/api.client';
import {splitCamelCase} from '../../../../../../utilities/Util';
import {Observable} from 'rxjs';
import {ClientService} from '../../../../../../services/ClientService';
import {switchMap, takeUntil} from 'rxjs/operators';
import {Disposable} from '../../../../../../utilities/Disposable';
import {AssistanceApplicationService} from '../../../../../public/application/assistance-application.service';
import {ActivatedRoute, Router} from '@angular/router';
import {AssistanceApplicationStatus} from '../../../../../../shared/enums/assistance-application-status';
import {NotificationService} from '../../../../../../services/NotificationService';
import {InternalDraftService} from '../../../../../../services/internal-draft.service';
import {SecurityService} from '../../../../../../services/SecurityService';
import swal from 'sweetalert2';

// One reusable list for every assistance-application type. The concrete type is supplied by
// the route (`data: { applicationType }`) — see routes.ts — instead of a per-component const.
@Component({
    selector: 'app-client-assistance-application',
    templateUrl: './client-assistance-application.component.html',
    styleUrls: ['./client-assistance-application.component.scss'],
    standalone: false
})
export class ClientAssistanceApplicationComponent extends Disposable implements OnInit {
    applicationType: AssistanceApplicationType;

    currentClient: Client;
    currentClient$: Observable<Client>;

    currentUser: User;
    currentUser$: Observable<User>;

    sortField = '';
    sortDirection = 'asc';

    clientAssistanceApplications: AssistanceApplication[];
    isClientCountyActive = true;
    invalidClientMessage: string;
    clientDrafts: InternalDraftApplicationViewModel[] = [];
    hasDraft = false;

    constructor(private assistanceApplicationService: AssistanceApplicationService,
                private _clientService: ClientService,
                private router: Router, private route: ActivatedRoute,
                private _notificationService: NotificationService,
                private internalDraftService: InternalDraftService,
                private _securityService: SecurityService) {
        super();
        this.applicationType = this.route.snapshot.data['applicationType'];
        this.currentClient$ = this._clientService.currentClient$;
        this.currentUser$ = this._securityService.currentUser$;
    }

    ngOnInit(): void {
        this.currentClient$
            .pipe(takeUntil(this.$destroyed))
            .subscribe(currentClient => this.currentClient = currentClient);
        this.currentUser$
            .pipe(takeUntil(this.$destroyed))
            .subscribe(currentUser => {
                this.currentUser = currentUser;
                const hasAccessToApplication = this.assistanceApplicationService.hasAccess(this.applicationType, currentUser);
                if (!hasAccessToApplication) {
                    this._notificationService.showError('You do not have access to view assistance applications.');
                    this.router.navigate(['/not-authorized']);
                    return;
                }
            });
        this.getClientAssistanceApplications(this.currentClient.id);
        this.loadClientDrafts();
        this.assistanceApplicationService.checkIsCaseCountyActive(this.currentClient.county, this.applicationType)
            .pipe(takeUntil(this.$destroyed))
            .subscribe((isActive: boolean) => this.isClientCountyActive = isActive || false);
    }

    isClientValid(): boolean {
        const isValid =
            !!this.currentClient?.birthYear ||
            !!this.currentClient?.gender ||
            !!this.currentClient?.employmentStatus ||
            !!this.currentClient?.houseHoldIncome ||
            !!this.currentClient?.houseHoldSize;

        if (!isValid) {
            this.invalidClientMessage = 'This Client Has Missing Info';
            return isValid;
        }

        if (!this.isClientCountyActive) {
            this.invalidClientMessage = `This Client's County is not accepting new applications.`;
        }

        if (this.hasDraft) {
            this.invalidClientMessage = 'A draft application exists for this client. Please complete or delete the draft before creating a new application.';
            return false;
        }

        return this.isClientCountyActive;
    }

    loadClientDrafts(): void {
        this.internalDraftService.getByClientAndType(this.currentClient.id, this.applicationType)
            .pipe(takeUntil(this.$destroyed))
            .subscribe({
                next: (drafts) => {
                    this.clientDrafts = drafts;
                    this.hasDraft = drafts.length > 0;
                },
                error: () => {
                    this._notificationService.showError('Error loading draft applications');
                }
            });
    }

    editDraft(draftId: string): void {
        this.router.navigate(['../add', this.applicationType], {
            relativeTo: this.route,
            queryParams: { draftId }
        }).then();
    }

    deleteDraft(draftId: string): void {
        swal.fire({
            title: 'Delete Draft?',
            text: 'Are you sure you want to delete this draft application? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            confirmButtonColor: '#d33'
        }).then((result) => {
            if (result.isConfirmed) {
                this.internalDraftService.delete(draftId).pipe(
                    switchMap(() => this.internalDraftService.getByClientAndType(this.currentClient.id, this.applicationType)),
                    takeUntil(this.$destroyed)
                ).subscribe({
                    next: (drafts) => {
                        this._notificationService.showSuccess('Draft application deleted.');
                        this.clientDrafts = drafts;
                        this.hasDraft = drafts.length > 0;
                    },
                    error: () => {
                        this._notificationService.showError('Error deleting draft application');
                    }
                });
            }
        });
    }

    getCaseStatus(status: string | number): string {
        if (typeof status === 'number') {
            return splitCamelCase(AssistanceApplicationStatus[status], true);
        }
        return splitCamelCase(status, true);
    }

    public navToDetail(id: string): void {
        this.router.navigate(['../', id], {relativeTo: this.route}).then();
    }

    sortTable(field: string): void {
        if (this.sortField === field) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortField = field;
            this.sortDirection = 'asc';
        }
        this.getClientAssistanceApplications(this.currentClient.id);
    }

    getClientAssistanceApplications(clientId: string): void {
        this.assistanceApplicationService.getClientAssistanceApplications(clientId, this.applicationType)
            .subscribe({
                next: (next) => {
                    this.clientAssistanceApplications = next.sort((a, b) => {
                        const aValue = this.resolveSortValue(a, this.sortField);
                        const bValue = this.resolveSortValue(b, this.sortField);
                        if (aValue < bValue) {
                            return this.sortDirection === 'asc' ? -1 : 1;
                        }
                        if (aValue > bValue) {
                            return this.sortDirection === 'asc' ? 1 : -1;
                        }
                        return 0;
                    });
                }
                ,
                error: () => {
                    this._notificationService.showError('Error Loading Assistance Applications');
                }
            });
    }

    // Resolves a possibly-dotted sort key (e.g. 'fundType.name', 'assignedAgency.agencyName')
    // against a row. The old per-type components used flat `a[field]`, so any nested column
    // silently failed to sort; walking the path makes those columns sort correctly.
    private resolveSortValue(row: AssistanceApplication, field: string): any {
        if (!field) {
            return undefined;
        }
        return field.split('.').reduce((value: any, key) => (value == null ? value : value[key]), row);
    }

    hasAccess(): boolean {
        return this.currentUser.userType !== UserType.Viewer;
    }
}
